import React from "react";
import { Eye, Pencil, User } from "lucide-react";

const formatColNum = (val) => {
  const num = Number(val || 0);
  if (isNaN(num)) return "0";
  return num.toLocaleString("en-IN");
};

export const getVerveProjectColumns = ({
  assigningId,
  setAssigningId,
  managers,
  handleAssignManager,
  setQuickViewId,
  navigate,
  handleEditProject,
  userRole,
}) => {
  const isFieldOfficer = userRole === 'installation_team';

  const columns = [
    {
      id: "verveId",
      header: "Verve ID",
      minWidth: "110px",
      render: (row) => (
        <span className="text-[12px] font-normal text-slate-700 uppercase tracking-tight whitespace-nowrap">
          {row.leadDetails?.customId || row.customId || row.leadDetails?.verveId || row.verveId || row.projectId || "—"}
        </span>
      ),
    },
    {
      id: "contactDetails",
      header: "Contact Details",
      minWidth: "220px",
      render: (row) => {
        const lead = row.leadDetails || row;
        const name = `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || lead.customerName || "—";
        return (
          <div className="flex flex-col min-w-0">
            <span className="font-normal text-slate-800 text-[13px] leading-tight truncate">
              {name}
            </span>
            <span className="text-[11px] text-slate-500 truncate mt-0.5">
              {lead.phoneNumber || "—"} <span className="text-slate-300 mx-1">|</span> {lead.email || "—"}
            </span>
          </div>
        );
      },
    },
    /*
    {
      id: "applicationId",
      header: "Customer Project ID",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.applicationId}
        </span>
      ),
    },
    {
      id: "projectName",
      header: "Customer",
      minWidth: "160px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight truncate" title={row.projectName}>
          {row.projectName || "—"}
        </span>
      ),
    },
    {
      id: "customerSiteId",
      header: "Customer Site ID",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.customerSiteId || "—"}
        </span>
      ),
    },
    {
      id: "siteId",
      header: "Site ID",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.siteId || "—"}
        </span>
      ),
    },
    {
      id: "siteName",
      header: "Site Name",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.siteName || "—"}
        </span>
      ),
    },
    */
    {
      id: "location",
      header: "Location",
      minWidth: "120px",
      render: (row) => {
        const ld = row.leadDetails || {};
        const l = row.leadId || {};
        const loc =
          row.location ||
          row.city ||
          row.district ||
          row.officeLocation ||
          row.state ||
          l.location ||
          l.city ||
          l.district ||
          l.officeLocation ||
          l.addressLine1 ||
          l.state ||
          ld.location ||
          ld.city ||
          ld.district ||
          ld.officeLocation ||
          row.address?.city ||
          row.addressLine1 ||
          "—";
        return (
          <div className="flex flex-col">
            <span className="text-[11px] font-medium text-slate-600 tracking-tight capitalize truncate" title={loc}>
              {loc}
            </span>
          </div>
        );
      }
    },
    {
      id: "createdDate",
      header: "Created date",
      minWidth: "140px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap tracking-tight">
          {row.createdAtDisplay}
        </span>
      ),
    },
    {
      id: "startDate",
      header: "Start date",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap tracking-tight">
          {row.startDateDisplay}
        </span>
      ),
    },
    /*
    {
      id: "poNumber",
      header: "PO Number",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.poNumber || "—"}
        </span>
      ),
    },
    {
      id: "value",
      header: "PO Value",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[12px] font-medium text-brand-blue whitespace-nowrap tabular-nums tracking-tight">
          {row.value}
        </span>
      ),
    },
    */
    {
      id: "projectValue",
      header: "Project value (₹)",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-700 whitespace-nowrap tabular-nums tracking-tight">
          {formatColNum(row.projectValue || row.targetCost || 0)}
        </span>
      ),
    },
    {
      id: "projectExpenses",
      header: "Project expenses (₹)",
      minWidth: "130px",
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-700 whitespace-nowrap tabular-nums tracking-tight">
          {formatColNum(row.totalExpenses || 0)}
        </span>
      ),
    },
    {
      id: "materialExpenses",
      header: "Material Expenses (₹)",
      minWidth: "140px",
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-700 whitespace-nowrap tabular-nums tracking-tight">
          {formatColNum(row.totalMaterialCost || 0)}
        </span>
      ),
    },
    {
      id: "amountReceived",
      header: "Amount recieved (₹)",
      minWidth: "130px",
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-700 whitespace-nowrap tabular-nums tracking-tight">
          {formatColNum(row.totalReceived || row.advancePaid || 0)}
        </span>
      ),
    },
    {
      id: "pendingBalance",
      header: "Pending balance (₹)",
      minWidth: "130px",
      render: (row) => {
        const bal = row.netBalance ?? ((row.projectValue || row.targetCost || 0) - (row.totalReceived || row.advancePaid || 0));
        return (
          <span className="text-[12px] font-medium text-slate-700 whitespace-nowrap tabular-nums tracking-tight">
            {formatColNum(bal)}
          </span>
        );
      },
    },
    {
      id: "ProfitLoss",
      header: "profit/loss (₹)",
      minWidth: "120px",
      render: (row) => {
        const pl = row.profitLoss ?? ((row.projectValue || row.targetCost || 0) - (row.totalExpenses || 0));
        const isNeg = pl < 0;
        return (
          <span className="text-[12px] font-medium text-slate-700 whitespace-nowrap tabular-nums tracking-tight">
            {isNeg ? '-' : ''}{formatColNum(Math.abs(pl))}
          </span>
        );
      },
    },
    {
      id: "projectManager",
      header: "Project Manager",
      minWidth: "160px",
      render: (row) =>
        row.managerId ? (
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-slate-800 text-[11px] leading-tight truncate tracking-tight">
              {row.projectManager}
            </span>
            <span className="text-[10px] text-brand-blue font-medium tracking-tight truncate">
              {row.pmPhone || "—"}
            </span>
          </div>
        ) : assigningId === row._id ? (
          <select
            className="w-full max-w-[140px] text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-md px-1.5 py-1 focus:border-brand-blue outline-none"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleAssignManager(row._id, e.target.value)}
            defaultValue=""
            autoFocus
            onBlur={() => setAssigningId(null)}
          >
            <option value="" disabled>
              Select manager
            </option>
            {managers.map((m) => (
              <option key={m._id} value={m._id}>
                {m.name}
              </option>
            ))}
          </select>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setAssigningId(row._id);
            }}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 border border-slate-100 text-[10px] font-medium text-slate-500 hover:text-brand-blue hover:border-brand-blue/30 transition-all tracking-tight"
          >
            <User className="w-3 h-3 shrink-0" />
            <span>Assign manager</span>
          </button>
        ),
    },
    {
      id: "status",
      header: "Project Status",
      minWidth: "110px",
      render: (row) => {
        const isAwaitingCompletion = row.completionApprovalStatus === 'Awaiting Approval' && row.pipelineStatus === 'Completed';
        return (
          <span
            className={`inline-flex whitespace-nowrap px-2 py-0.5 rounded-md text-[10px] font-medium border tracking-tight ${isAwaitingCompletion
              ? "bg-amber-50 text-amber-700 border-amber-100"
              : row.pipelineStatus === "Completed"
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : "bg-brand-blue/5 text-brand-blue border-brand-blue/10"
              }`}
          >
            {isAwaitingCompletion ? "Awaiting Completion" : row.pipelineStatus}
          </span>
        );
      },
    },
    {
      id: "progress",
      header: "Progress",
      minWidth: "100px",
      render: (row) => (
        <div className="flex items-center gap-2 min-w-[80px]">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-blue"
              style={{ width: `${Math.min(100, row.progress ?? 0)}%` }}
            />
          </div>
          <span className="text-[11px] font-medium text-slate-500 shrink-0 tabular-nums">
            {row.progress ?? 0}%
          </span>
        </div>
      ),
    },
    /* Commenting out remaining columns
    {
      id: "srType",
      header: "SR Type",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.srType || "—"}
        </span>
      ),
    },
    {
      id: "structureType",
      header: "Kitchen Layout",
      minWidth: "110px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap tracking-tight">
          {row.projectSummary}
        </span>
      ),
    },
    {
      id: "applicationDate",
      header: "Allocation Date",
      minWidth: "110px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap tracking-tight">
          {row.applicationDateDisplay}
        </span>
      ),
    },
    {
      id: "siteStatus",
      header: "Site Status",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.siteStatus || "—"}
        </span>
      ),
    },
    {
      id: "commencementDetails",
      header: "Commencement Details",
      minWidth: "140px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.commencementDetails || "—"}
        </span>
      ),
    },
    {
      id: "remarks",
      header: "Remarks",
      minWidth: "140px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight truncate" title={row.remarks}>
          {row.remarks || "—"}
        </span>
      ),
    },
    {
      id: "wccNumber",
      header: "WCC Number",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.wccNumber || "—"}
        </span>
      ),
    },
    {
      id: "wccStatus",
      header: "WCC Status",
      minWidth: "100px",
      render: (row) => (
        <span className={`text-[11px] font-medium tracking-tight ${row.wccStatus === 'Approved' ? 'text-green-600' : row.wccStatus === 'Rejected' ? 'text-red-600' : 'text-amber-600'}`}>
          {row.wccStatus || "—"}
        </span>
      ),
    },
    {
      id: "invoiceNumber",
      header: "Invoice Number",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 tracking-tight">
          {row.invoiceNumber || "—"}
        </span>
      ),
    },
    {
      id: "invoiceDate",
      header: "Invoice Date",
      minWidth: "110px",
      render: (row) => {
        if (!row.invoiceDate) return <span className="text-slate-400">—</span>;
        return (
          <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap tracking-tight">
            {new Date(row.invoiceDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        );
      },
    },
    {
      id: "invoiceStatus",
      header: "Invoice Status",
      minWidth: "100px",
      render: (row) => (
        <span className={`text-[11px] font-medium tracking-tight ${row.invoiceStatus === 'Paid' ? 'text-green-600' : row.invoiceStatus === 'Overdue' ? 'text-red-600' : row.invoiceStatus === 'Cancelled' ? 'text-slate-400' : 'text-amber-600'}`}>
          {row.invoiceStatus || "—"}
        </span>
      ),
    },
    {
      id: "capacity",
      header: "Capacity",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap tracking-tight">
          {row.capacity || "—"}
        </span>
      ),
    },
    {
      id: "budget",
      header: "Budget",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap tabular-nums tracking-tight">
          {row.targetCostDisplay}
        </span>
      ),
    },
    {
      id: "balance",
      header: "Balance",
      minWidth: "100px",
      render: (row) => (
        <span
          className={`text-[12px] font-medium whitespace-nowrap tabular-nums tracking-tight ${row.balancePaidOff ? "text-emerald-600" : "text-rose-600"}`}
        >
          {row.balance}
        </span>
      ),
    },
    {
      id: "startDate",
      header: "Start Date",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap tracking-tight">
          {row.startDateDisplay}
        </span>
      ),
    },
    {
      id: "expectedEndDate",
      header: "Expected End Date",
      minWidth: "120px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap tracking-tight">
          {row.expectedEndDateDisplay}
        </span>
      ),
    },
    {
      id: "created",
      header: isFieldOfficer ? "Created" : "Created Date",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[11px] font-medium text-slate-400 whitespace-nowrap tracking-tight">
          {row.createdAtDisplay}
        </span>
      ),
    },
    */
    {
      id: "actions",
      header: "Actions",
      minWidth: "110px",
      render: (row) => {
        if (isFieldOfficer) {
          const DISPATCHED_STATUSES = [
            "Dispatched",
            "Project started",
            "Civil Complete",
            "Electrical Complete",
            "Complete",
            "Balance Update",
            "Documents",
            "Meter Connection",
            "Subsidy",
            "Completed",
          ];
          const isDispatched = DISPATCHED_STATUSES.some(
            (s) => s.toLowerCase() === (row.status || "").toLowerCase()
          );

          return (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                disabled={!isDispatched}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDispatched) navigate(`/projects/${row._id}`);
                }}
                className={`p-1.5 cursor-pointer rounded-md transition-colors ${isDispatched
                  ? "text-brand-blue hover:bg-brand-blue/10"
                  : "text-slate-200 cursor-not-allowed opacity-50"
                  }`}
                title={isDispatched ? "View project" : "Access starts at dispatch"}
              >
                <Eye className="w-4 h-4" />
              </button>
            </div>
          );
        }

        return (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setQuickViewId(row._id);
              }}
              className="p-1.5 text-slate-400 cursor-pointer hover:text-brand-blue transition-colors rounded-md hover:bg-brand-blue/5"
              title="Quick view"
            >
              <Eye className="w-4 h-4" />
            </button>

            {/* <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEditProject(row);
              }}
              className="p-1.5 text-slate-400 cursor-pointer hover:text-brand-blue transition-colors rounded-md hover:bg-brand-blue/5"
              title="Edit project"
            >
              <Pencil className="w-4 h-4" />
            </button> */}
          </div>
        );
      },
    },
  ];

  if (isFieldOfficer) {
    const fieldOfficerColumnIds = [
      "contactDetails",
      "verveId",
      "srType",
      "location",
      "createdDate",
      "startDate",
      "structureType",
      "siteStatus",
      "commencementDetails",
      "remarks",
      "projectValue",
      "projectExpenses",
      "materialExpenses",
      "amountReceived",
      "pendingBalance",
      "profitLoss",
      "status",
      "wccNumber",
      "wccStatus",
      "invoiceNumber",
      "invoiceDate",
      "invoiceStatus",
      "applicationDate",
      "projectManager",
      "progress",
      "capacity",
      "created",
      "actions",
    ];
    return columns.filter((col) => fieldOfficerColumnIds.includes(col.id));
  }

  return columns.filter((col) => col.id !== "capacity");
};
